import React, { createContext, useContext, useEffect, useState } from "react";

export interface DocumentationFilePathContextType {
  documentationFilePath: string | null;
  hasEditor: boolean;
  setDocumentationFilePath: (path: string | null) => void;
  setHasEditor: (hasEditor: boolean) => void;
}

const DocumentationFilePathContext =
  createContext<DocumentationFilePathContextType>({
    documentationFilePath: null,
    hasEditor: false,
    setDocumentationFilePath: () => {},
    setHasEditor: () => {},
  });

export const DocumentationFilePathProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [documentationFilePath, setDocumentationFilePath] = useState<
    string | null
  >(null);
  const [hasEditor, setHasEditor] = useState<boolean>(false);

  // Event listener for receiving file path from documentation iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security (you may want to adjust this based on your documentation URL)
      if (
        event.origin !== window.location.origin &&
        !event.origin.includes("d32lhafmwoak77.cloudfront.net")
      ) {
        return;
      }

      // Check if the message contains a file path for download
      if (event.data && event.data.type === "DOCUMENTATION_FILE_PATH") {
        setDocumentationFilePath(event.data.filePath);
        setHasEditor(event.data.hasEditor || false);
        console.log(
          "Received file path from documentation:",
          event.data.filePath,
          "hasEditor:",
          event.data.hasEditor
        );
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return (
    <DocumentationFilePathContext.Provider
      value={{
        documentationFilePath,
        hasEditor,
        setDocumentationFilePath,
        setHasEditor,
      }}
    >
      {children}
    </DocumentationFilePathContext.Provider>
  );
};

export const useDocumentationFilePath = () => {
  const context = useContext(DocumentationFilePathContext);
  if (!context) {
    throw new Error(
      "useDocumentationFilePath must be used within a DocumentationFilePathProvider"
    );
  }
  return context;
};

export { DocumentationFilePathContext };
